import { useState, useEffect } from 'react';
import { Calendar, MapPin, ExternalLink, Loader2, CalendarDays, Clock, ArrowRight } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import SEO from '../SEO';
import { getStaticData } from '../../hooks/useStaticData';
import { combineSchemas, getBreadcrumbSchema, getEventSchema } from '../../utils/structuredData';

import { eventPath, eventTypeColor, eventTypeLabel, formatDate, formatTime, isUpcoming } from './eventDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_EVENTS_DESCRIPTION =
  'Martial arts tournaments, seminars, belt tests and special events from Owaza Martial Arts in Spring Hill, FL.';

const getEventsDescription = (nextEvent) => {
  if (!nextEvent) return DEFAULT_EVENTS_DESCRIPTION;
  const date = new Date(nextEvent.date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const venue = (nextEvent.location || '').split(',')[0].trim();
  return `Next up: ${nextEvent.title}, ${date}${venue ? ` at ${venue}` : ''}. Tournaments, seminars and special events from Owaza Martial Arts.`;
};

// Build-time snapshot of upcoming events. The react-snap prerender blocks API
// calls (the fetch fails), so this is what puts Event JSON-LD into the static
// /events HTML; it's used whenever the live fetch hasn't succeeded.
const STATIC_EVENTS = getStaticData('events')?.items || [];

const EventsSEO = ({ events }) => {
  const upcomingEvents = events
    .filter((event) => isUpcoming(event))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <SEO
      title="Events & Tournaments"
      description={getEventsDescription(upcomingEvents[0])}
      canonical="/events"
      ogImage={upcomingEvents[0]?.image_url || undefined}
      jsonLd={combineSchemas(
        getBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Events', path: '/events' },
        ]),
        ...upcomingEvents.map((event) => getEventSchema(event, eventPath(event)))
      )}
    />
  );
};

const EventCard = ({ event }) => {
  const upcoming = isUpcoming(event);
  const detailsHref = eventPath(event);

  return (
    <Card className={`flex flex-col overflow-hidden ${!upcoming ? 'opacity-75' : ''}`}>
      {event.image_url && (
        // Posters are portrait; letterbox them on a dark backdrop instead of cropping.
        <a href={detailsHref} className="block bg-gray-900 p-3" tabIndex={-1} aria-hidden="true">
          <img
            src={event.image_url}
            alt={`${event.title} poster`}
            className="mx-auto h-72 w-full object-contain"
          />
        </a>
      )}
      <CardContent className="flex flex-1 flex-col p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <Badge className={eventTypeColor(event)}>{eventTypeLabel(event)}</Badge>
          </div>
          {!upcoming && (
            <Badge variant="secondary">Past Event</Badge>
          )}
        </div>

        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          <a href={detailsHref} className="hover:text-red-700 hover:underline">{event.title}</a>
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">{event.description}</p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <CalendarDays className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span>{formatDate(event.date)}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Clock className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span>{formatTime(event.date)}</span>
            {event.end_date && (
              <span className="text-gray-500 dark:text-gray-400">- {formatTime(event.end_date)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <MapPin className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span>{event.location}</span>
          </div>
        </div>

        <div className="mt-auto space-y-3">
          <a
            href={detailsHref}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-red-600 px-4 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
          >
            View details
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>

          {upcoming && event.registration_url && event.registration_open && (
            <a
              href={event.registration_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-red-600 hover:bg-red-700">
                <ExternalLink className="w-4 h-4 mr-2" />
                Register Now
              </Button>
            </a>
          )}

          {upcoming && event.registration_url && !event.registration_open && (
            <Button disabled className="w-full">
              Registration Closed
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await fetch(`${API}/events?include_past=${showPast}`);
        if (!response.ok) {
          throw new Error('Failed to load events');
        }
        const data = await response.json();
        setEvents(data.items || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [showPast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <EventsSEO events={STATIC_EVENTS} />
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  // When the API is unreachable (including the react-snap prerender), list the
  // build-time snapshot so the cards and their detail links are still there.
  const staticUpcoming = STATIC_EVENTS.filter((event) => isUpcoming(event));
  const useStaticList = Boolean(error) && !showPast && staticUpcoming.length > 0;
  const listedEvents = useStaticList ? staticUpcoming : events;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <EventsSEO events={error ? STATIC_EVENTS : events} />
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <Calendar className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Upcoming Events
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Join us for tournaments, seminars, belt tests, and special events at
            Owaza Martial Arts
          </p>
        </div>

        {/* Filter Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-1 bg-white dark:bg-gray-800">
            <button
              onClick={() => setShowPast(false)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                !showPast
                  ? 'bg-red-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setShowPast(true)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                showPast
                  ? 'bg-red-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              All Events
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && !useStaticList && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <p className="text-red-600 text-center">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Events Grid */}
        {listedEvents.length > 0 ? (
          <div className={`grid gap-6 ${listedEvents.length === 1 ? 'mx-auto max-w-md' : 'md:grid-cols-2'}`}>
            {listedEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <CalendarDays className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No Upcoming Events
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Check back soon for new tournaments, seminars, and special events!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <div className="mt-12 text-center text-gray-500">
          <p className="mb-2">
            Questions about events? Contact us:
          </p>
          <p className="font-medium text-gray-700">
            (352) 428-0086 | info@owazamartialarts.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
